/**
 * #13 電子送禮 API client
 *
 * - 4 步驟 wizard 收尾 POST /api/gifts
 * - GET /api/gifts/:id 兩種 view:sender / recipient(欄位不同)
 * - POST /api/gifts/:id/receive 由收禮人從 LIFF /gift/receive/:id 觸發
 */
import { api } from "./client";
import type { Address } from "./members";
import { WALLET_CATEGORY_NAMES, WALLET_CATEGORY_UNITS } from "./wallet";

export type GiftChannel = "beneficiary" | "phone" | "line_friend";
export type GiftTemplate = "elegant" | "festival" | "minimal";
export type GiftStatus =
  | "pending_payment_mock"
  | "paid"
  | "delivered"
  | "received"
  | "cancelled";


export interface GiftItem {
  product_id: string;
  name: string;
  spec: string;
  price_each: number;
  bottle_count: number;
  qty: number;
  subtotal: number;
  wallet_category?: string;  // 存摺品類 snapshot(giftbox 以「件=組」計;"" = 不進存摺)
}

export interface BlessingCard {
  template: GiftTemplate;
  message: string;
}

// 後端只回投影,不回整包 gift doc(backend/services/gift_service.py 的 recipient_view / sender_view,2026-10-06):
//   - 任何人的 LINE userId(sender_uid / recipient_member_uid / recipient_line_uid)都不回 ——
//     要判斷身分或關聯,用後端算好的布林(preview 的 is_sender / is_recipient、sender view 的 recipient_is_member)
//   - 收禮人看的(preview / 領取 / 我收到的)不含金額、recipient_name、note;宅配領取人的手機地址兩邊都不回
// 要讀新欄位時後端投影要一起加,不然讀到的永遠是 undefined。
export interface Gift {
  gift_id: string;
  short_code?: string;            // 2026-05-26 新增,channel-less 分享 URL 用
  sender_name: string;
  recipient_name?: string;        // sender view 才有:送禮人自己標的「給誰」(僅您可見)
  recipient_is_member?: boolean;  // sender view 才有:收禮人已關聯霽雲會員
  items: GiftItem[];
  total?: number;                 // recipient view 隱藏,只 sender view 才有
  total_bottles: number;
  // 品類明細 {cat: qty}(2026-07-04 送什麼扣什麼品類)— 舊禮物無此欄
  category_lines?: Record<string, number>;
  blessing_card: BlessingCard;
  status: GiftStatus;
  note?: string;                  // sender view 才有:送禮人自己的內部備註
  created_at?: string;
  received_at?: string;
}

export interface GiftCreatePayload {
  items: { product_id: string; qty: number }[];
  blessing_template: GiftTemplate;
  blessing_message?: string;
  // 全部可選(channel-less 改版後,sender 不必預先輸入 recipient):
  recipient_name?: string;         // 自己看的標記(「給張小姐生日」)
  recipient_phone?: string;        // 有填則 backend try link 既有 member
  recipient_channel?: GiftChannel | "link";  // backward compat
  shipping_address?: Address | null;
  note?: string;
}

// 祝福卡 template 中文 label + 預設文案(對齊後端 gift_service.DEFAULT_MESSAGES)
export const GIFT_TEMPLATES: { key: GiftTemplate; label: string; subtitle: string; defaultMessage: string }[] = [
  {
    key: "elegant",
    label: "優雅",
    subtitle: "為日常珍貴時刻",
    defaultMessage: "願這份滋養,陪您度過時光中的每一刻珍貴。",
  },
  {
    key: "festival",
    label: "節慶",
    subtitle: "佳節 / 紀念日",
    defaultMessage: "佳節將至,以霽雲之名,獻上最深的祝福。",
  },
  {
    key: "minimal",
    label: "簡約",
    subtitle: "靜默的心意",
    defaultMessage: "誠摯獻上。",
  },
];

export const GIFT_STATUS_LABELS: Record<GiftStatus, string> = {
  pending_payment_mock: "等待付款",
  paid: "已付款 · 等待送出",
  delivered: "已送出",
  received: "已領取",
  cancelled: "已取消",
};

export const GIFT_CHANNEL_LABELS: Record<GiftChannel, string> = {
  beneficiary: "權益人",
  phone: "手機聯絡",
  line_friend: "LINE 好友",
};

/**
 * 品類明細 → 顯示字串,例「即飲 6 瓶 · 留白禮盒 2 組」。
 * 舊禮物(無 category_lines)fallback 回「N 瓶」。
 */
export function formatGiftLines(gift: Pick<Gift, "category_lines" | "total_bottles">): string {
  const lines = gift.category_lines || {};
  const parts = Object.entries(lines)
    .filter(([, qty]) => qty > 0)
    .map(([cat, qty]) => `${WALLET_CATEGORY_NAMES[cat] || cat} ${qty} ${WALLET_CATEGORY_UNITS[cat] || ""}`.trim());
  if (parts.length) return parts.join(" · ");
  return `${gift.total_bottles || 0} 瓶`;
}

/**
 * 單一 gift item 的數量顯示 — giftbox 以「件=組」計,其餘 qty×bottle_count 再配該品類的單位。
 * 單位不可以寫死「瓶」:燕盞(dry)是「盒」,2026-09-20 加第四品類時踩到。
 */
export function formatGiftItemQty(it: GiftItem): string {
  if (it.wallet_category === "giftbox") return `${it.qty} 組`;
  const unit = WALLET_CATEGORY_UNITS[it.wallet_category || ""] || "瓶";
  return `${it.qty * (it.bottle_count || 0)} ${unit}`;
}


export async function createGift(payload: GiftCreatePayload): Promise<{
  status: string; gift_id?: string; short_code?: string; gift?: Gift;
  // insufficient_balance 時的品類資訊(送什麼扣什麼品類)
  category?: string; category_name?: string; unit?: string; balance?: number; needed?: number;
  // no_wallet_items 時:是哪幾項商品不進存摺(用來指名道姓告訴客人要換掉哪個)
  names?: string[];
}> {
  const { data } = await api.post("/api/gifts", payload);
  return data;
}

export async function getGift(id_or_code: string): Promise<{ gift: Gift; view: "sender" | "recipient"; is_recipient_member?: boolean }> {
  const { data } = await api.get(`/api/gifts/${id_or_code}`);
  return data;
}

/**
 * 公開預覽(2026-05-26 channel-less 重構新增)— 不需 LINE login。
 * 收禮人開 share link 進到 LIFF `/gift/receive/:short_code` 時的初始 fetch。
 * 回值不含金額(避免破壞「拆禮」儀式感)。
 */
/**
 * 後端用 4xx 傳「為什麼不行」—— cannot_receive_own_gift / invalid_phone /
 * already_received_by_other 等,body 裡都有 status 欄位。
 *
 * ⚠️ axios 預設會把 4xx 丟成例外,呼叫端的 catch 就一律吐「連線異常,請稍後再試」,
 * 真正的理由被整包吃掉。2026-09-16 謝睿朋領不到禮物就是這樣:後端明明回了
 * cannot_receive_own_gift,GiftReceive 裡也早就寫好中文對照,但一句都跑不到
 * (詳 NOTES.md)。admin.ts 早有同樣的修正,送禮這支當時漏了。
 *
 * 401 不放行 —— 那要讓 client.ts 的攔截器清 JWT。
 */
const PASS_4XX = (s: number) => (s >= 200 && s < 300) || s === 400 || s === 403 || s === 404;

/**
 * 公開預覽。有登入(client.ts 會自動帶 JWT)時,後端多回 is_sender / is_recipient —
 * 呼叫端靠這兩個布林判斷「這是不是我自己送的」,不必拿到任何人的 uid。
 */
export async function previewGiftByCode(
  short_code: string,
): Promise<{ gift: Gift; is_sender?: boolean; is_recipient?: boolean }> {
  const { data } = await api.get(`/api/gifts/preview/${short_code}`);
  return data;
}

export async function listMyGiftsSent(): Promise<Gift[]> {
  const { data } = await api.get("/api/gifts/sent");
  return data.gifts;
}

export async function listMyGiftsReceived(): Promise<Gift[]> {
  const { data } = await api.get("/api/gifts/received");
  return data.gifts;
}

export async function receiveGift(gift_id: string): Promise<{ status: string; gift?: Gift }> {
  const { data } = await api.post(`/api/gifts/${gift_id}/receive`, undefined, {
    validateStatus: PASS_4XX,
  });
  return data;
}

/**
 * 非會員實體寄送路徑(2026-05-26 channel-less C 方案)。
 * 不需 active member,但需 LINE login(JWT 帶 line_user_id);填姓名 + 手機 + 地址。
 */
export async function claimGiftByShipping(
  identifier: string,
  payload: {
    recipient_name: string;
    recipient_phone: string;
    shipping_address: { city: string; district: string; detail: string };
  },
): Promise<{ status: string; gift?: Gift }> {
  const { data } = await api.post(`/api/gifts/${identifier}/claim-shipping`, payload, {
    validateStatus: PASS_4XX,
  });
  return data;
}
